import { useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import SubHeader from "@/components/SubHeader";
import { Reveal, Chapter } from "@/components/Reveal";
import SimWebsiteBuilder from "@/components/sim/SimWebsiteBuilder";
import SimPackageBuilder from "@/components/sim/SimPackageBuilder";
import SimInbox from "@/components/sim/SimInbox";
import SimQuote from "@/components/sim/SimQuote";

const ListMock = ({ rows }) => (
  <div className="space-y-2 rounded-xl border border-sand bg-white p-4">
    {rows.map(([a, b]) => (
      <div key={a} className="flex items-center justify-between rounded-lg bg-paper px-3.5 py-2.5">
        <span className="text-xs font-semibold text-ink">{a}</span>
        <span className="font-mono text-[10px] text-stone2">{b}</span>
      </div>
    ))}
  </div>
);

const TableMock = ({ cols, rows }) => (
  <div className="overflow-x-auto rounded-xl border border-sand bg-white">
    <table className="w-full min-w-[380px] text-left text-xs">
      <thead><tr className="border-b border-sand font-mono text-[9px] uppercase tracking-wider text-stone2">
        {cols.map((c) => <th key={c} className="px-3.5 py-2.5 font-semibold">{c}</th>)}
      </tr></thead>
      <tbody className="divide-y divide-sand/60">
        {rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="px-3.5 py-2.5 text-ink">{c}</td>)}</tr>)}
      </tbody>
    </table>
  </div>
);

const ChartMock = () => (
  <div className="rounded-xl border border-sand bg-white p-4">
    <div className="mb-3 flex items-center justify-between">
      <p className="text-xs font-bold text-ink">Visitors · views · inquiries</p>
      <span className="rounded-full bg-gold/15 px-2 py-0.5 font-mono text-[9px] font-bold uppercase text-gold">Sample dashboard data</span>
    </div>
    <div className="h-44">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={[{m:"Jan",v:30},{m:"Feb",v:44},{m:"Mar",v:39},{m:"Apr",v:58},{m:"May",v:52},{m:"Jun",v:71},{m:"Jul",v:84}]} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
          <defs><linearGradient id="pf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C49A5A" stopOpacity={0.35} /><stop offset="100%" stopColor="#C49A5A" stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid stroke="#EFECE6" vertical={false} />
          <XAxis dataKey="m" tick={{ fontSize: 9, fill: "#57616F" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: "#57616F" }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E6DFD0", fontSize: 11 }} />
          <Area type="monotone" dataKey="v" stroke="#C49A5A" strokeWidth={2.5} fill="url(#pf)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const SECTIONS = [
  {
    id: "dmc-operations", title: "DMC Operations",
    problem: "Daily work scattered across Excel, documents, WhatsApp and email.",
    feature: "One operating workspace for products, rates, packages, inquiries and customers.",
    benefit: "Your team stops chasing files and starts working from a single source.",
    workflow: "Add → Manage → Operate",
    visual: <ListMock rows={[["Destinations", "42"], ["Hotels", "118"], ["Packages", "128"], ["Open inquiries", "17"]]} />,
  },
  {
    id: "product-management", title: "Product Management",
    problem: "Product information lives in multiple files and versions.",
    feature: "Destinations, hotels, activities and transfers as structured, reusable inventory.",
    benefit: "Every product is complete, current and ready to sell or share.",
    workflow: "Create → Enrich → Reuse",
    visual: <ListMock rows={[["Velassaru Maldives", "★★★★★ · Published"], ["Sunset Cruise", "Private · $120"], ["Airport → Resort", "Speedboat · Pvt"]]} />,
  },
  {
    id: "rate-management", title: "Rate Management",
    problem: "Rate sheets go stale the moment you send them.",
    feature: "Contract rates, seasons, markups, supplements, validity and currency in one place.",
    benefit: "Centralize your rates — partners always see the latest information you're ready to share.",
    workflow: "Update → Set validity → Publish",
    visual: <TableMock cols={["Product", "Season", "Rate"]} rows={[["Water Villa", "High", "$420"], ["Beach Villa", "High", "$310"], ["Sunset Cruise", "All", "$120"]]} />,
  },
  {
    id: "package-builder", title: "Package Builder",
    problem: "Every itinerary starts from a blank document.",
    feature: "Drag days into place from your own inventory — hotels, activities, transfers, meals.",
    benefit: "Complete multi-day packages in minutes, priced your way.",
    workflow: "Build → Price → Publish",
    visual: <SimPackageBuilder />,
  },
  {
    id: "inquiry-management", title: "Inquiry Management",
    problem: "Requests arrive on WhatsApp, email and calls — and get lost.",
    feature: "Every inquiry enters one dashboard with statuses, assignment and conversation.",
    benefit: "Nothing slips. Every request has an owner and a next step.",
    workflow: "Receive → Assign → Respond",
    visual: <SimInbox />,
  },
  {
    id: "quotation-engine", title: "Quotation Engine",
    problem: "Quotes are assembled manually and sent as scattered messages.",
    feature: "Itemized, branded proposals built from the inquiry's requirements.",
    benefit: "Professional quotes in minutes — responses come back into the same workflow.",
    workflow: "Build → Preview → Send",
    visual: <SimQuote />,
  },
  {
    id: "partner-distribution", title: "Partner Distribution",
    problem: "Partners keep asking for your latest rates and packages.",
    feature: "A structured channel where authorized B2B partners access current products and rates.",
    benefit: "Update once — the information your partners see stays aligned.",
    workflow: "Publish → Grant access → Partners browse",
    visual: <TableMock cols={["Partner", "Access", "Last active"]} rows={[["Asia Trails Co.", "Rates + Packages", "Today"], ["Gulf Gateway", "Packages", "Yesterday"], ["Nordic Resor", "Rates", "2 days ago"]]} />,
  },
  {
    id: "agent-channel", title: "Travel Agent Channel",
    problem: "Intermediaries own the travel-agent relationship.",
    feature: "A direct digital channel where agents discover products, inquire and request quotes.",
    benefit: "More direct relationships, better visibility, a new sales channel.",
    workflow: "Discover → Inquire → Quote → Book (future)",
    visual: <ListMock rows={[["Discover destinations", "Live"], ["Request quotes", "Live"], ["Manage inquiries", "Live"], ["Direct booking", "Roadmap"]]} />,
  },
  {
    id: "white-label-website", title: "White-Label Website",
    problem: "Your digital presence doesn't match your on-ground reputation.",
    feature: "A branded website on your own domain — logo, colors, imagery, content.",
    benefit: "Your brand at the front. Travel-Tek behind it. Try it below.",
    workflow: "Brand → Content → Publish",
    visual: <SimWebsiteBuilder />,
  },
  {
    id: "crm", title: "CRM",
    problem: "Customer context disappears after each trip.",
    feature: "Every inquirer becomes a customer record with history and preferences.",
    benefit: "Repeat business starts from knowledge, not from scratch.",
    workflow: "Inquiry → Customer → Repeat",
    visual: <TableMock cols={["Customer", "Trips", "Last inquiry"]} rows={[["Rahul Sharma", "1", "Feb 2027"], ["Sarah Chen", "2", "Mar 2027"]]} />,
  },
  {
    id: "analytics", title: "Analytics",
    problem: "Decisions run on gut feel instead of numbers.",
    feature: "Visitors, package views, inquiries and conversion — per destination.",
    benefit: "See what sells, adjust, grow.",
    workflow: "Measure → Adjust → Grow",
    visual: <ChartMock />,
  },
];

export default function Platform() {
  useEffect(() => {
    if (window.location.hash) {
      const t = setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView(), 500);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="platform-page">
      <SubHeader />
      <main>
        <section className="container-x pb-10 pt-32">
          <Reveal><Chapter index={1} label="The Platform" /></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-8 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl">
              The Digital Operating & Distribution Platform for DMCs
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-stone2 sm:text-lg">
              Every module below connects a feature to the problem it removes and the
              business benefit it creates. Interactive sections use labeled sample data.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <div className="mt-8 flex flex-wrap gap-2">
              {SECTIONS.map((s) => (
                <a key={s.id} href={`#${s.id}`} data-testid={`platform-jump-${s.id}`}
                  className="rounded-full border border-sand bg-white px-3.5 py-1.5 text-xs font-semibold text-stone2 transition-colors hover:border-terra hover:text-ink">
                  {s.title}
                </a>
              ))}
            </div>
          </Reveal>
        </section>

        {SECTIONS.map((s, i) => (
          <section key={s.id} id={s.id} className={`scroll-mt-24 border-t border-sand py-16 lg:py-20 ${i % 2 ? "bg-parch/60" : ""}`} data-testid={`platform-section-${s.id}`}>
            <div className="container-x grid grid-cols-1 items-start gap-10 lg:grid-cols-2">
              <Reveal className={i % 2 ? "lg:order-2" : ""}>
                <span className="font-mono text-xs font-bold tracking-[0.22em] text-terra">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="mt-3 font-serif text-2xl tracking-tight text-ink sm:text-3xl">{s.title}</h2>
                <div className="mt-5 space-y-3">
                  <p className="text-sm leading-relaxed text-stone2"><span className="font-mono text-[10px] font-bold uppercase tracking-widest text-stone2/70">Problem · </span>{s.problem}</p>
                  <p className="text-sm leading-relaxed text-ink"><span className="font-mono text-[10px] font-bold uppercase tracking-widest text-terra">Feature · </span>{s.feature}</p>
                  <p className="text-sm leading-relaxed text-ink"><span className="font-mono text-[10px] font-bold uppercase tracking-widest text-emerald2">Benefit · </span>{s.benefit}</p>
                </div>
                <p className="mt-5 inline-flex rounded-full bg-white px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-stone2 shadow-sm">
                  {s.workflow}
                </p>
              </Reveal>
              <Reveal delay={0.1} className={i % 2 ? "lg:order-1" : ""}>
                {s.visual}
              </Reveal>
            </div>
          </section>
        ))}

        <section className="border-t border-sand py-16 lg:py-20" data-testid="platform-infrastructure">
          <div className="container-x">
            <Reveal>
              <h2 className="max-w-2xl font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                You Run the Destination. We Run the Technology.
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-stone2">
                A fully managed platform so your team can focus on selling destinations
                instead of managing technology.
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
              {["Domain", "Cloud hosting", "Database", "Media storage", "CDN", "SSL", "Backups", "Security", "Monitoring", "Software updates"].map((it) => (
                <div key={it} className="rounded-xl border border-sand bg-white px-4 py-3.5 text-xs font-semibold text-ink" data-testid={`platform-infra-${it.toLowerCase().replace(/\s/g, "-")}`}>
                  {it}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-sand bg-parch/60 py-16 lg:py-20" data-testid="platform-roadmap">
          <div className="container-x grid grid-cols-1 gap-8 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-2xl bg-ink p-7">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-terra">Today</p>
                <div className="mt-5 grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                  {["DMC operations", "Product management", "Rate management", "Package builder", "Inquiry management", "Quotation engine", "Partner distribution", "Agent channel"].map((n) => (
                    <p key={n} className="flex items-center gap-2.5 border-b border-white/10 py-2.5 text-sm text-paper/85">
                      <span className="text-terra">✓</span> {n}
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-2xl border border-dashed border-terra/50 bg-white p-7">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-stone2">Coming next · roadmap, not yet live</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {["Online booking", "Payments", "Hotel APIs", "Activity APIs", "Supplier integrations", "B2B agent booking", "Dynamic packaging", "WhatsApp automation", "AI-powered operations"].map((n) => (
                    <span key={n} className="rounded-full border border-sand bg-paper px-3.5 py-2 text-xs font-medium text-ink">{n}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="border-t border-sand bg-ink py-16 text-center">
          <div className="container-x">
            <h2 className="font-serif text-3xl text-paper">See it working for your DMC.</h2>
            <a href="/#demo" data-testid="platform-demo-cta"
              className="mt-7 inline-flex rounded-full bg-terra px-8 py-4 text-sm font-bold text-ink transition-colors duration-300 hover:bg-paper">
              Book a Demo
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
